import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { useAuth } from '../auth.jsx';
import { permittedSections, readOpened, sectionOf, writeOpened } from '../lib/admin-nav.js';
import { addonOn } from '../modules.js';
import { CommandPalette, openCommandPalette } from './CommandPalette.jsx';
import { useCan } from './guards.jsx';
import {
  BoxIcon,
  CardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  GamepadIcon,
  GearIcon,
  GlobeIcon,
  InvoiceIcon,
  KeyIcon,
  PageIcon,
  PeopleIcon,
  PuzzleIcon,
  SearchIcon,
  TicketIcon,
} from './icons.jsx';
import { NavItem } from './NavItem.jsx';
import { NotificationBell } from './NotificationBell.jsx';
import { TentacleMark } from './Tentacle.jsx';
import { ThemePicker } from './ThemePicker.jsx';

const SECTION_ICONS = {
  clients: PeopleIcon,
  billing: InvoiceIcon,
  services: GamepadIcon,
  domains: GlobeIcon,
  support: TicketIcon,
  catalog: BoxIcon,
  content: PageIcon,
  billing_settings: CardIcon,
  staff: KeyIcon,
  system: GearIcon,
  integrations: PuzzleIcon,
};

/** The sections permitted for this staff member, from every module's adminNav. */
export function useAdminSections() {
  const can = useCan();
  const { addons } = useAuth();
  return permittedSections(can, { show: (i) => addonOn(i, addons) });
}

/**
 * A sidebar link, with a count next to it when the entry has `badge: { key, load, label(n) }` (the
 * overdue tasks): loaded once a minute in the background, hidden at zero.
 */
function AdminLink({ item }) {
  const count = useQuery({
    queryKey: ['admin-nav-badge', item.badge?.key],
    queryFn: () => item.badge.load(),
    enabled: Boolean(item.badge),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  });
  const n = Number(count.data) || 0;
  return (
    <NavItem to={item.to}>
      {n ? (
        <span className="flex items-center justify-between gap-2">
          <span className="truncate">{item.label}</span>
          <span className="rounded-full bg-accent px-1.5 text-xs font-semibold text-on-accent tabular-nums">
            <span aria-hidden="true">{n > 99 ? '99+' : n}</span>
            <span className="sr-only">{item.badge.label(n)}</span>
          </span>
        </span>
      ) : (
        item.label
      )}
    </NavItem>
  );
}

/**
 * The admin links in foldable sections. Only the section of the page on screen opens by itself; a
 * section opened by hand stays open (remembered per staff member in this browser), and folding it
 * forgets that again.
 */
function AdminNav({ sections, staffId }) {
  const { pathname } = useLocation();
  const active = sectionOf(pathname, sections);
  const [state, setState] = useState(() => ({ staffId, active, opened: readOpened(staffId), foldedActive: false }));
  let current = state;
  if (state.staffId !== staffId || state.active !== active) {
    current = {
      staffId,
      active,
      opened: state.staffId === staffId ? state.opened : readOpened(staffId),
      foldedActive: false,
    };
    setState(current);
  }
  useEffect(() => writeOpened(state.staffId, state.opened), [state.staffId, state.opened]);

  const isOpen = (key) => (key === current.active && !current.foldedActive) || current.opened.includes(key);
  function toggle(key) {
    setState((s) => {
      const open = (key === s.active && !s.foldedActive) || s.opened.includes(key);
      return open
        ? { ...s, opened: s.opened.filter((k) => k !== key), foldedActive: key === s.active ? true : s.foldedActive }
        : { ...s, opened: [...s.opened, key], foldedActive: key === s.active ? false : s.foldedActive };
    });
  }

  return (
    <>
      <NavItem to="/admin" end>
        Dashboard
      </NavItem>
      {sections
        .filter((s) => s.flat)
        .flatMap((s) => s.items)
        .map((l) => (
          <AdminLink key={l.to} item={l} />
        ))}
      {sections
        .filter((s) => !s.flat)
        .map((section) => {
          const Icon = SECTION_ICONS[section.key] ?? GearIcon;
          const open = isOpen(section.key);
          const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
          return (
            <div key={section.key} className="mt-2">
              <button
                type="button"
                id={`nav-${section.key}-label`}
                aria-expanded={open}
                aria-controls={`nav-${section.key}`}
                onClick={() => toggle(section.key)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm hover:text-white ${section.key === current.active ? 'font-semibold text-white' : 'text-white/70'}`}
              >
                <Icon className="text-base text-white/60" />
                <span className="flex-1 text-left">{section.label}</span>
                <Chevron className="text-white/50" />
              </button>
              <div
                id={`nav-${section.key}`}
                role="group"
                aria-labelledby={`nav-${section.key}-label`}
                hidden={!open}
                className="mt-0.5 ml-5 border-l border-white/10 pl-2"
              >
                {section.items.map((l) => (
                  <AdminLink key={l.to} item={l} />
                ))}
              </div>
            </div>
          );
        })}
    </>
  );
}

/** "Search" at the top of the sidebar: opens the command palette (also Ctrl+K / ⌘K). */
function SearchButton() {
  return (
    <button
      type="button"
      onClick={openCommandPalette}
      aria-keyshortcuts="Control+K Meta+K"
      className="mb-3 flex w-full items-center gap-2 rounded-md border border-white/15 px-3 py-1.5 text-sm text-white/70 hover:border-white/30 hover:text-white"
    >
      <SearchIcon />
      <span className="flex-1 text-left">Search</span>
      <kbd className="font-mono text-xs text-white/60">Ctrl K</kbd>
    </button>
  );
}

/** The admin area's frame: the same ink sidebar as the client area, with the staff sections. */
export function AdminShell() {
  const { me, company, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuButton = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sections = useAdminSections();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      menuButton.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);

  async function onLogout() {
    await logout();
    navigate('/admin/login');
  }

  return (
    <div className="min-h-screen md:grid md:grid-cols-[15rem_1fr]">
      <a
        href="#main"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
        className="sr-only z-50 rounded-md bg-surface px-3 py-2 text-sm font-semibold text-text shadow focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      <aside
        aria-label="Admin sidebar"
        className="bg-ink text-white md:sticky md:top-0 md:flex md:h-screen md:flex-col"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-4">
          <Link to="/admin" className="flex min-w-0 items-center gap-2 font-bold">
            <TentacleMark />
            <span className="truncate">{company.name}</span>
            <span className="rounded bg-white/10 px-1.5 text-xs font-semibold text-white/70">Admin</span>
          </Link>
          <NotificationBell realm="staff" className="ml-auto" />
          <div className="flex items-center gap-1 md:hidden">
            <button
              type="button"
              className="rounded-md p-2 text-white/80 hover:text-white"
              aria-label="Search"
              onClick={openCommandPalette}
            >
              <SearchIcon />
            </button>
            <button
              ref={menuButton}
              type="button"
              className="rounded-md px-2 py-1 text-sm text-white/80"
              aria-expanded={open}
              aria-controls="admin-nav"
              onClick={() => setOpen((o) => !o)}
            >
              {open ? 'Close' : 'Menu'}
            </button>
          </div>
        </div>
        <nav
          id="admin-nav"
          aria-label="Admin"
          className={`${open ? 'block' : 'hidden'} px-3 pb-4 md:block md:min-h-0 md:flex-1 md:overflow-y-auto`}
        >
          <div className="hidden md:block">
            <SearchButton />
          </div>
          <AdminNav sections={sections} staffId={me?.staff?.uuid} />
        </nav>
        <div className={`${open ? 'flex' : 'hidden'} border-t border-white/10 px-4 pt-3 md:flex`}>
          <ThemePicker realm="staff" tone="ink" />
        </div>
        <div className={`${open ? 'flex' : 'hidden'} items-center justify-between gap-2 px-4 py-3 md:flex`}>
          <Link to="/admin/account" className="min-w-0 truncate font-mono text-xs text-white/70 hover:text-white">
            {me?.staff?.email}
          </Link>
          <button type="button" className="text-sm text-white/80 hover:text-white" onClick={onLogout}>
            Log out
          </button>
        </div>
      </aside>
      <div className="flex min-h-screen min-w-0 flex-col">
        <main id="main" tabIndex={-1} className="w-full min-w-0 flex-1 px-4 py-8 focus:outline-none md:px-10">
          <Outlet />
        </main>
      </div>
      <CommandPalette sections={sections} />
    </div>
  );
}
